import { KANMON_CONNECT, KanmonConnectEnviroment } from '@kanmon/web-sdk';
import { getKanmonInfo, getKanmonToken } from 'api/common';
import TinyStore from 'lib/tinyStore';
import ProviderStore from 'store/Provider';
import { EKanmonUserState, TKanmonInfo } from 'types/common';
import { isProductionEnv } from 'utils/common';
import { EDot, getUserId, setHasViewDotPage } from 'utils/localstore';

export type KanmonUiState = {
    status: 'idle' | 'loading' | 'ready' | 'error';
    userState?: EKanmonUserState;
    actionMessage?: string | null;
    actionRequired?: boolean;
};
type Snapshot = {
    providerId?: number;
    info?: TKanmonInfo;
    connect?: typeof KANMON_CONNECT;
    loading: boolean;
    ui: KanmonUiState;
    productsRevision: number;
};
const emptySnapshot = (providerId?: number): Snapshot => ({
    providerId, loading: false, ui: { status: 'idle' }, productsRevision: 0,
});
const Store = TinyStore.createStore<{ snapshot: Snapshot }>({
    nameSpace: 'klarity', key: 'kanmon', crossBundle: false, runTime: true,
    initValue: { snapshot: emptySnapshot() },
});
let snapshot = emptySnapshot();
let generation = 0;
let pending: Promise<void> | undefined;
let stateTimeout: ReturnType<typeof setTimeout> | undefined;
const subscribers = new Set<{ providerId: number; onHide: () => void }>();

const update = (patch: Partial<Snapshot>) => {
    snapshot = { ...snapshot, ...patch };
    Store.set('snapshot', snapshot);
};
const stop = () => {
    clearTimeout(stateTimeout);
    if (snapshot.connect) KANMON_CONNECT.stop();
};
const reset = (providerId?: number) => {
    generation += 1;
    stop();
    pending = undefined;
    snapshot = emptySnapshot(providerId);
    Store.set('snapshot', snapshot);
};
const isCurrent = (providerId: number, version: number) => (
    generation === version && snapshot.providerId === providerId && (ProviderStore.get('data')?.id ?? getUserId()) === providerId
);

export async function refreshKanmonInfo(
    providerId: number,
    { createIfMissing = false, refreshAfterPending = false }: { createIfMissing?: boolean; refreshAfterPending?: boolean } = {},
): Promise<void> {
    const version = generation;
    if (!isCurrent(providerId, version)) return;
    if (pending) {
        await pending;
        if (!isCurrent(providerId, version)) return;
        // Another caller may have started creation while we awaited the initial GET.
        if (createIfMissing && !snapshot.info?.kanmonBusinessId) return refreshKanmonInfo(providerId, { createIfMissing: true });
        // Explicit refreshes (including after mutations) must not reuse a pre-mutation response.
        if (refreshAfterPending) return refreshKanmonInfo(providerId);
        return;
    }
    const create = createIfMissing && !snapshot.info?.kanmonBusinessId;
    update({ loading: true, ui: snapshot.connect ? snapshot.ui : { status: 'loading' } });
    const request = (async () => {
        try {
            const res = await (create ? getKanmonToken() : getKanmonInfo());
            if (!isCurrent(providerId, version)) return;
            if (res.error || !res.data?.data) throw new Error('Unable to load financing');
            const info: TKanmonInfo = res.data.data;
            update({ info });
            if (!info.kanmonBusinessId && !info.connectToken) {
                update({ ui: { status: 'idle' } });
                return;
            }
            if (snapshot.connect) return;
            if (!info.connectToken) throw new Error('Missing financing session');
            setHasViewDotPage(EDot.FINANCIALS_TAG_1128, 'true');
            update({ connect: KANMON_CONNECT, ui: { status: 'loading' } });
            const fail = () => {
                if (!isCurrent(providerId, version)) return;
                stop();
                // Reject late callbacks and responses from this failed session.
                generation += 1;
                pending = undefined;
                update({ connect: undefined, loading: false, ui: { status: 'error' } });
            };
            stateTimeout = setTimeout(fail, 15000);
            KANMON_CONNECT.start({
                environment: isProductionEnv() ? KanmonConnectEnviroment.PRODUCTION : KanmonConnectEnviroment.SANDBOX,
                connectToken: info.connectToken,
                onError: fail,
                onEvent: (event) => {
                    if (!isCurrent(providerId, version)) return;
                    if (event.eventType === 'USER_STATE_CHANGED') {
                        clearTimeout(stateTimeout);
                        const changed = snapshot.ui.userState !== event.data.userState;
                        update({
                            ui: {
                                status: 'ready', userState: event.data.userState as EKanmonUserState,
                                actionMessage: event.data.actionMessage, actionRequired: event.data.actionRequired,
                            },
                            productsRevision: snapshot.productsRevision + (changed && event.data.userState === 'SERVICING' ? 1 : 0),
                        });
                        if (changed) refreshKanmonInfo(providerId);
                    } else if (event.eventType === 'HIDE') {
                        update({ productsRevision: snapshot.productsRevision + 1 });
                        subscribers.forEach((subscriber) => {
                            if (subscriber.providerId === providerId) subscriber.onHide();
                        });
                        refreshKanmonInfo(providerId);
                    }
                },
            });
        } catch {
            if (isCurrent(providerId, version)) {
                stop();
                generation += 1;
                pending = undefined;
                update({ connect: undefined, loading: false, ui: { status: 'error' } });
            }
        } finally {
            if (isCurrent(providerId, version)) update({ loading: false });
        }
    })();
    pending = request;
    await request;
    if (pending === request) pending = undefined;
}

export function acquireKanmon(providerId: number, onHide: () => void): () => void {
    if (snapshot.providerId !== providerId) reset(providerId);
    const subscriber = { providerId, onHide };
    subscribers.add(subscriber);
    // Joining a healthy session must not reset its iframe or callbacks.
    if (!snapshot.info && !pending) refreshKanmonInfo(providerId);
    return () => {
        subscribers.delete(subscriber);
        if (snapshot.providerId === providerId && !Array.from(subscribers).some((item) => item.providerId === providerId)) reset();
    };
}

export default Store;
